import React from "react";
import { Link } from "react-router-dom";
import { formatBoardDate, houseNames } from "../../utils/homepageSeason";
import { tournamentPath } from "../../utility/tournamentUrls";

const rowLabel = (tournament, nextId) => {
  if (tournament.isLive) return "On the lanes";
  if (tournament.id === nextId) return "Next";
  return "";
};

const rowHref = (tournament) => {
  if (tournament.isCompleted) {
    return tournamentPath(tournament, "/standings");
  }
  return tournamentPath(tournament);
};

const TournamentRow = ({ tournament, nextId, flyer = false }) => {
  const houses = houseNames(tournament);
  const completed = Boolean(tournament.isCompleted);

  return (
    <Link
      to={rowHref(tournament)}
      className={`group grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 py-5 border-b border-brand-charcoal/15 items-baseline transition-colors ${
        flyer
          ? "bg-brand-gold/10 -mx-4 px-4 md:mx-0 md:px-3 hover:bg-brand-gold/20"
          : "-mx-4 px-4 md:mx-0 md:px-3 hover:bg-white"
      }`}
    >
      <span className="md:col-span-2 font-display tracking-widest uppercase text-xs text-brand-gold-ink">
        {rowLabel(tournament, nextId)}
      </span>
      <span
        className={`md:col-span-5 font-display font-bold group-hover:underline underline-offset-4 decoration-brand-gold ${
          flyer ? "text-2xl" : "text-xl"
        }`}
      >
        {tournament.name}
        {completed ? (
          <span className="block mt-1 font-display font-bold text-sm group-hover:underline underline-offset-4">
            Standings
          </span>
        ) : null}
      </span>
      <span className="md:col-span-3 text-brand-muted">
        {houses.map((name) => (
          <span key={name} className="block">
            {name}
          </span>
        ))}
        {tournament.organization && !houses.includes(tournament.organization) ? (
          <span className="block text-sm mt-0.5">
            {tournament.organization}
          </span>
        ) : null}
      </span>
      <span className="md:col-span-2 font-score md:text-right">
        {formatBoardDate(
          completed
            ? tournament.lastDate
            : tournament.firstDate || tournament.startDate
        )}
      </span>
    </Link>
  );
};

const SeasonList = ({ upcoming = [], recent = [] }) => {
  const nextId = upcoming.find((tournament) => tournament.isUpcoming)?.id;
  const isEmpty = upcoming.length === 0 && recent.length === 0;

  return (
    <section className="bg-brand-cream py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isEmpty ? (
          <>
            <h2 className="heading-display text-3xl md:text-4xl">Upcoming</h2>
            <p className="mt-4 text-brand-muted max-w-xl">
              Last standings stay on the board above. When the next date is set,
              it shows up here.
            </p>
          </>
        ) : (
          <>
            {upcoming.length > 0 ? (
              <div>
                <h2 className="heading-display text-3xl md:text-4xl">
                  Upcoming
                </h2>
                <div className="mt-8 border-t border-brand-charcoal/15">
                  {upcoming.map((tournament) => (
                    <TournamentRow
                      key={tournament.id}
                      tournament={tournament}
                      nextId={nextId}
                      flyer
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {recent.length > 0 ? (
              <div className={upcoming.length > 0 ? "mt-14" : ""}>
                <h2 className="heading-display text-3xl md:text-4xl">Recent</h2>
                <div className="mt-8 border-t border-brand-charcoal/15">
                  {recent.map((tournament) => (
                    <TournamentRow
                      key={tournament.id}
                      tournament={tournament}
                      nextId={nextId}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
};

export default SeasonList;
